import { Culture } from "@kolibrisoftware/customerportal-api";
import { useTranslation } from "@kolibrisoftware/hooks-util";
import { ParsedRow } from "helpers/template-builder/types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "store/helpers";
import { selectObjectPreviewData } from "store/object-preview/selectors";
import { RiskData, RiskStatusResponse } from "types/foundation-risk";

const useFoundationRiskIndicator = () => {
  const previewData = useSelector(selectObjectPreviewData);
  const culture = useSelector(state => state.main.culture);
  const t = useTranslation();
  const [foundationRiskData, setFoundationRiskData] = useState<RiskData>();

  const fetchData = useCallback(async () => {
    const address = previewData?.address;

    if (!address) {
      return;
    }

    try {
      let url = `https://funderi.nl/api/risk-status?postcode=${address.postalCode}&huisnummer=${address.houseNumber}`;

      if (address.houseNumberPostfix) {
        url += `&toevoeging=${address.houseNumberPostfix}`;
      }

      // We know the API key is publicly visible at this point in time.
      // There is no infra in place to obfuscate it through a back-end proxy.
      const res = await fetch(url, {
        headers: {
          "X-Api-Key": "t47yE5qOczGaZYtso3lyVi1TkqrTlvQK",
        },
      });

      const response: RiskStatusResponse = await res?.json();
      if (response?.success && response?.data) {
        setFoundationRiskData(response?.data);
      }
    } catch {
      // Do nothing and keep the data as undefined to show the empty state.
    }
  }, [previewData?.address]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const mappedFoundationRiskDetails: ParsedRow[] = useMemo(() => {
    return [
      {
        label: t("objectPreview.details.foundationRisk.indicator"),
        option: {
          value:
            foundationRiskData?.riskIndicator ??
            t("objectPreview.details.foundationRisk.noData"),
        },
      },
      {
        label: t("objectPreview.details.foundationRisk.explanation"),
        option: {
          value:
            culture === Culture.En
              ? foundationRiskData?.riskLabel_en
              : foundationRiskData?.riskLabel ?? "-",
        },
      },
    ];
  }, [foundationRiskData, culture, t]);

  return { mappedFoundationRiskDetails };
};

export default useFoundationRiskIndicator;
